import { AccessControlsSheetContent } from "@/features/management-settings/components/access-controls-sheet-content";
import { showAlertCallout } from "@/shared/alerts";
import { useConfirmPopoverStore, useSheetStore } from "@/shared/stores";
import {
  getFindAccessControlsQueryKey,
  useDeleteAccessControlById,
  useFindAccessControls,
} from "@/spz-api/access-controls/access-controls";
import { type SubjectDto, SubjectDtoType } from "@/spz-api/model";
import { Typography } from "@spz/ui/components/typography";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
interface SelectAccessControlViewModelProps {
  subjectId?: SubjectDto["id"];
  subjectType?: SubjectDtoType;
}

interface OpenAccessControlSheetParams {
  subjectId: SubjectDto["id"];
  subjectType: SubjectDtoType;
  subjectName: string;
}

export const selectAccessControlViewModel = ({
  subjectId,
  subjectType,
}: SelectAccessControlViewModelProps) => {
  const { openSheet } = useSheetStore();
  const queryClient = useQueryClient();
  const { closePopover } = useConfirmPopoverStore();

  const { t } = useTranslation();
  const params =
    subjectType === SubjectDtoType.USER
      ? { userId: subjectId }
      : subjectType === SubjectDtoType.USER_GROUP
        ? { userGroupId: subjectId }
        : {};

  const {
    data,
    isPending: accessControlsPending,
    error: accessControlsError,
  } = useFindAccessControls(params, {
    query: {
      enabled: Boolean(subjectType && subjectId),
    },
  });

  const { mutate: deleteACLMutation } = useDeleteAccessControlById({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("success_ACL_deleted"),
        });

        closePopover();

        queryClient.invalidateQueries({
          queryKey: getFindAccessControlsQueryKey({}),
        });
      },
      onError: () => {
        showAlertCallout({
          type: "critical",
          title: t("error_ACL_delete"),
        });

        closePopover();
      },
    },
  });

  const handleDelete = (id: string) => {
    deleteACLMutation({
      id,
    });
  };

  const openAccessControlSheet = ({
    subjectName,
    subjectId,
    subjectType,
  }: OpenAccessControlSheetParams) => {
    openSheet({
      title: <Typography trans="access_controls" />,
      content: (
        <AccessControlsSheetContent
          subjectName={subjectName}
          subjectId={subjectId}
          subjectType={subjectType}
        />
      ),
    });
  };

  return {
    openAccessControlSheet,
    directAccessControls: data?.directAccessControls ?? [],
    inheritedAccessControls: data?.inheritedAccessControls ?? [],
    accessControlsPending,
    accessControlsError,
    deleteACLMutation,
    handleDelete,
  };
};
